import {
  Box,
  BoxProps,
  ButtonGroup,
  HStack,
  Icon,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useCallback, useMemo, useState } from 'react';
import {
  RiDeleteBinLine,
  RiEyeLine,
  RiMoneyDollarCircleLine,
} from 'react-icons/ri';
import { ConfirmationModal } from '../../../../../../components/ConfirmationModal';
import { LinkButton } from '../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../components/Pagination';
import { AppointmentPaymentConfirmationModal } from '../../../../../../components/AppointmentPaymentConfirmationModal';
import { TableFilters } from '../../../../../../components/TableFilters';
import { useAuth } from '../../../../../../hooks/auth';
import { IAddressBase } from '../../../../../../models/address';
import { PrettyAppointmentPaymentStatus } from '../../../../../../models/appointments';
import {
  createAppointmentExternalPaymentsService,
  IAppointmentPayment,
} from '../../../../../../services/Appointments/CreateAppointmentExternalPaymentsService';
import deleteAppointmentsService from '../../../../../../services/Appointments/DeleteAppointmentsService';
import { IAppointmentListItem } from '../../../../../../services/Appointments/ListAppointmentsService';
import { IDetailedAppointment } from '../../../../../../services/Appointments/ShowAppointmentsService';
import { translateError } from '../../../../../../utils/errors';

type SelectOption = {
  label: string;
  value: string;
};

export interface IHandleAppointmentPayment {
  address?: IAddressBase;
  appointmentId: string;
  payment?: IAppointmentPayment;
}

export interface IAppointmentTableItem extends IAppointmentListItem {
  formattedCreatedAt: string;
  formattedTotalValue: string;
}

interface IAppointmentTableProps extends BoxProps {
  appointments: IAppointmentTableItem[];
  currentPage?: number;
  filterBy?: string;
  filterByOptions?: SelectOption[];
  onChangeFilterBy?: (value: string) => void;
  onDelete: (appointment: IAppointmentTableItem) => void;
  onFilter?: (value: string) => void;
  onPageChange?: (page: number) => void;
  onPay?: (data: IDetailedAppointment) => void;
  totalPages?: number;
}

export const AppointmentTable = ({
  appointments,
  currentPage,
  filterBy,
  filterByOptions = [
    {
      label: 'Nome do usuário',
      value: 'userName',
    },
    {
      label: 'Cadastrado em',
      value: 'createdAt',
    },
  ],
  onChangeFilterBy,
  onDelete,
  onFilter,
  onPageChange,
  onPay,
  totalPages,
  ...rest
}: IAppointmentTableProps): JSX.Element => {
  const toast = useToast();

  const { assignment: authenticatedAssignment } = useAuth();

  const [handlingAppointment, setHandlingAppointment] =
    useState<IAppointmentTableItem>();
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);

  const cancelAppointmentWarningMessage = useMemo(() => {
    if (!handlingAppointment) {
      return undefined;
    }

    if (handlingAppointment?.paymentStatus === 'PAYMENT_EXTERNAL') {
      return 'Essa ordem de serviço foi paga no clube, o reembolso deverá ser feito externamente.';
    }

    return undefined;
  }, [handlingAppointment]);

  const togglePaymentModal = useCallback(
    (appointment?: IAppointmentTableItem) => {
      setHandlingAppointment(appointment);

      setIsPaymentModalVisible((prevState) => !prevState);
    },
    [],
  );

  const toggleDeleteConfirmationModal = (
    appointment?: IAppointmentTableItem,
  ): void => {
    setHandlingAppointment(appointment);

    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  };

  const handleAppointmentPayment = useCallback(
    async ({ appointmentId, payment }: IHandleAppointmentPayment) => {
      if (!onPay || !payment) {
        return;
      }

      try {
        const paidAppointment = await createAppointmentExternalPaymentsService({
          appointmentId,
          payment: {
            authorizationCode: payment.authorizationCode,
            authorizedAt: payment.authorizedAt,
            type: payment.type,
            acquirerName: payment.acquirerName,
            acquirerNsu: payment.acquirerNsu,
            brand: payment.brand,
          },
        });

        onPay(paidAppointment);

        togglePaymentModal(undefined);

        toast({
          title: 'Transação concluída com sucesso',
          description: 'A transação foi efetuada corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          if (err.response?.data.message === 'user-address-missing') {
            toast({
              title: 'Cadastro incompleto',
              description: `Para registrar o pagamento será necessário completar o cadastro do membro. ${translateError(
                { message: err.response?.data.message },
              )}`,
              status: 'warning',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });

            return;
          }

          toast({
            title: 'Falha na transação',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao efetuar a transação, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [togglePaymentModal, onPay, toast],
  );

  const deleteAppointment = async (
    appointment: IAppointmentTableItem,
  ): Promise<void> => {
    try {
      await deleteAppointmentsService(appointment.id);

      onDelete(appointment);

      toast({
        title: 'Cancelado com sucesso',
        description: 'A ordem de serviço foi cancelada corretamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'subtle',
        position: 'top-right',
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao cancelar',
          description:
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao cancelar a ordem de serviço, tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    } finally {
      toggleDeleteConfirmationModal(undefined);
    }
  };

  return (
    <Box {...rest}>
      {!!handlingAppointment && (
        <AppointmentPaymentConfirmationModal
          isOpen={isPaymentModalVisible}
          onClose={() => togglePaymentModal(undefined)}
          onConfirm={(payment) =>
            handleAppointmentPayment({
              appointmentId: handlingAppointment.id,
              payment,
            })
          }
          user={handlingAppointment.user}
        />
      )}

      {!!handlingAppointment && (
        <ConfirmationModal
          isOpen={!!isDeleteConfirmationModalVisible}
          onClose={() => toggleDeleteConfirmationModal(undefined)}
          onConfirm={() => deleteAppointment(handlingAppointment)}
          title="Confirmar cancelamento"
          message="Deseja realmente cancelar?"
          warningMessage={cancelAppointmentWarningMessage}
        />
      )}

      <HStack align="flex-end" spacing="4">
        <TableFilters
          filterBy={filterBy}
          filterByOptions={filterByOptions}
          filterInputType={filterBy === 'createdAt' ? 'datepicker' : 'input'}
          onChangeFilterBy={onChangeFilterBy}
          onFilter={onFilter}
        />
      </HStack>

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th textAlign="center" w="8">
              #OS
            </Th>

            <Th>Usuário</Th>

            <Th>Valor total</Th>

            <Th>Status pagamento</Th>

            <Th textAlign="center">Cadastrado em</Th>

            <Th textAlign="right" w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {appointments.map((appointment) => {
            const isPaymentButtonDisabled =
              ![
                'WAITING',
                'FAILED',
                'REJECTED',
                'PAYMENT_EXTERNAL_PENDING',
              ].includes(appointment.paymentStatus) ||
              !authenticatedAssignment?.featureGroup.features.some(
                (feature) =>
                  ![
                    'APPOINTMENT_PAYMENT_FULL_ACCESS',
                    'APPOINTMENT_PAYMENT_WRITE_ACCESS',
                  ].includes(feature.key),
              );

            let paymentButtonColorScheme = isPaymentButtonDisabled
              ? 'green'
              : 'red';

            if (appointment.paymentStatus === 'PAYMENT_EXTERNAL_PENDING') {
              paymentButtonColorScheme = 'yellow';
            }

            return (
              <Tr key={appointment.id}>
                <Td textAlign="center" w="8">
                  <Text>{String(appointment.code).padStart(6, '0')}</Text>
                </Td>

                <Td>
                  <Text>{appointment.user.name}</Text>
                </Td>

                <Td>
                  <Text>{appointment.formattedTotalValue}</Text>
                </Td>

                <Td>
                  <Text>
                    {PrettyAppointmentPaymentStatus[appointment.paymentStatus]}
                  </Text>
                </Td>

                <Td textAlign="center">
                  <Text>{appointment.formattedCreatedAt}</Text>
                </Td>

                <Td textAlign="right">
                  <ButtonGroup>
                    {/* <LinkButton
                      px={0}
                      colorScheme="green"
                      to={{
                        pathname: '/services/appointments/management',
                        state: {
                          appointmentId: appointment.id,
                        },
                      }}
                    >
                      <Icon as={RiRefreshLine} fontSize="18" />
                    </LinkButton> */}

                    {!!appointment.totalValue &&
                      !!onPay &&
                      authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'APPOINTMENT_PAYMENT_FULL_ACCESS',
                            'APPOINTMENT_PAYMENT_READ_ACCESS',
                            'APPOINTMENT_PAYMENT_WRITE_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <IconButton
                          aria-label="Payment"
                          size="sm"
                          icon={
                            <Icon as={RiMoneyDollarCircleLine} fontSize="18" />
                          }
                          fontSize="16"
                          colorScheme={paymentButtonColorScheme}
                          color="white"
                          disabled={isPaymentButtonDisabled}
                          onClick={() => togglePaymentModal(appointment)}
                        />
                      )}

                    <LinkButton
                      px={0}
                      to={{
                        pathname: '/services/appointments/details',
                        state: {
                          appointmentId: appointment.id,
                        },
                      }}
                    >
                      <Icon as={RiEyeLine} fontSize="16" />
                    </LinkButton>

                    {appointment.paymentStatus !== 'CANCELED' &&
                      authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'APPOINTMENT_FULL_ACCESS',
                            'APPOINTMENT_DELETE_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <IconButton
                          aria-label="delete-appointment"
                          size="sm"
                          icon={<Icon as={RiDeleteBinLine} fontSize="16" />}
                          colorScheme="red"
                          onClick={() =>
                            toggleDeleteConfirmationModal(appointment)
                          }
                        />
                      )}
                  </ButtonGroup>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
